/**
 * UserProfilePage - Persönliche Einstellungen
 * Enthält ai_reply_style_prompt für KI-Antwortstil
 * Nur für eingeloggte Benutzer (nicht Gäste)
 */

import { useState, useEffect, useCallback } from 'react';
import { User, Save, RefreshCw, Wand2, ToggleLeft, ToggleRight, Info } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UserProfilePage = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [profile, setProfile] = useState({
    username: '',
    name: '',
    role: '',
    ai_reply_style_prompt: '',
    ai_reply_style_enabled: true
  });

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser).token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = getToken();
      await axios.put(`${API}/auth/profile`, {
        ai_reply_style_prompt: profile.ai_reply_style_prompt,
        ai_reply_style_enabled: profile.ai_reply_style_enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Einstellungen gespeichert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleStyleEnabled = () => {
    setProfile(prev => ({
      ...prev,
      ai_reply_style_enabled: !prev.ai_reply_style_enabled
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center">
            <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mein Profil</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Persönliche Einstellungen für {profile.name}
            </p>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 text-green-700 dark:text-green-300">
            {success}
          </div>
        )}

        {/* User Info Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Benutzerinformationen</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Benutzername</span>
              <p className="font-medium text-gray-900 dark:text-white">{profile.username}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Name</span>
              <p className="font-medium text-gray-900 dark:text-white">{profile.name}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Rolle</span>
              <p className="font-medium text-gray-900 dark:text-white capitalize">
                {profile.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
              </p>
            </div>
          </div>
        </div>

        {/* AI Reply Style Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Wand2 className="h-5 w-5 text-purple-500" />
              <h2 className="font-semibold text-gray-900 dark:text-white">KI-Antwortstil</h2>
            </div>
            <button
              onClick={toggleStyleEnabled}
              className="flex items-center gap-2 text-sm"
              data-testid="toggle-ai-style"
            >
              {profile.ai_reply_style_enabled ? (
                <>
                  <ToggleRight className="h-6 w-6 text-green-500" />
                  <span className="text-green-600 dark:text-green-400">Aktiv</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="h-6 w-6 text-gray-400" />
                  <span className="text-gray-500">Deaktiviert</span>
                </>
              )}
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">Was ist der KI-Antwortstil?</p>
                <p className="text-blue-600 dark:text-blue-400">
                  Diese Anweisung wird bei jeder KI-generierten Antwort (z.B. E-Mail-Entwürfe) als Style-Vorgabe 
                  verwendet. So können Sie den Ton, die Formalität und besondere Formulierungen Ihrer 
                  automatischen Antworten anpassen.
                </p>
              </div>
            </div>
          </div>

          {/* Textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Persönliche Style-Anweisung
            </label>
            <textarea
              value={profile.ai_reply_style_prompt}
              onChange={(e) => setProfile(prev => ({ ...prev, ai_reply_style_prompt: e.target.value }))}
              placeholder="z.B.: Antworte freundlich und professionell. Verwende &quot;Sie&quot; als Anrede. Unterschreibe mit &quot;Mit freundlichen Grüßen, Ulrich Mitza&quot;."
              rows={6}
              disabled={!profile.ai_reply_style_enabled}
              className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none ${
                !profile.ai_reply_style_enabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="ai-reply-style-prompt"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Lassen Sie das Feld leer, um den neutralen Standardstil zu verwenden.
            </p>
          </div>

          {/* Example */}
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Beispiel-Anweisungen:</p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
              <li>Antworte immer auf Deutsch, formell mit &quot;Sie&quot;</li>
              <li>Halte Antworten kurz und präzise</li>
              <li>Verwende die Signatur: &quot;GB Sylt Immobilienverwaltung&quot;</li>
              <li>Bei Rechnungsanfragen verweise auf die Buchhaltung</li>
            </ul>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Einstellungen speichern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
